import { Accordion, AccordionDetails, AccordionSummary, makeStyles } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_ARCHITECTURE,
  DEFAULT_MEMORY,
  DEFAULT_RUNTIME,
  DEFAULT_SNAPSTART_ENABLED,
  DEFAULT_SNAP_ALIAS_ENABLED,
  DEFAULT_TIMEOUT,
} from 'config-editor-common';
import type { LambdaDefaults } from 'config-editor-common';
import { LambdaDefaultsHeader } from './LambdaDefaultsHeader';
import { LambdaFieldsEditor } from './LambdaFieldsEditor';
import { LambdaIamSection } from './LambdaIamSection';

// Re-export for backward compatibility
export type { LambdaDefaults };

interface LambdaDefaultsEditorProps {
  defaults?: LambdaDefaults;
  onChange: (defaults: LambdaDefaults) => void;
  isAuthorizer?: boolean; // Indicates if this is for authorizer lambdas
  /** IAM policies at group level (not in defaults object) */
  iamPolicyNames?: string[];
  customIamPolicyNames?: string[];
  onIamPolicyNamesChange?: (policies: string[]) => void;
  onCustomIamPolicyNamesChange?: (policies: string[]) => void;
}

const useStyles = makeStyles(theme => ({
  defaultsSection: {
    marginBottom: theme.spacing(3),
    '&:before': {
      display: 'none',
    },
  },
  defaultsSummary: {
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px dashed ${theme.palette.divider}`,
    minHeight: 48,
    '&.Mui-expanded': {
      minHeight: 48,
    },
  },
  defaultsDetails: {
    flexDirection: 'column',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderTop: `1px dashed ${theme.palette.divider}`,
  },
}));

export const LambdaDefaultsEditor = ({
  defaults: propsDefaults,
  onChange,
  isAuthorizer = false,
  iamPolicyNames = [],
  customIamPolicyNames = [],
  onIamPolicyNamesChange,
  onCustomIamPolicyNamesChange,
}: LambdaDefaultsEditorProps) => {
  const classes = useStyles();

  // Default snapstart value: true for authorizers, false for endpoints
  const defaultSnapstart = isAuthorizer ? true : DEFAULT_SNAPSTART_ENABLED;

  // Memoize initial defaults to avoid recreating on every render
  const initialDefaults: LambdaDefaults = useMemo(() => {
    if (!propsDefaults) {
      return {
        runtime: DEFAULT_RUNTIME,
        architecture: DEFAULT_ARCHITECTURE,
        lambda_source: '',
        memory_size: DEFAULT_MEMORY,
        timeout: DEFAULT_TIMEOUT,
        snapstart: defaultSnapstart,
        snap_alias: DEFAULT_SNAP_ALIAS_ENABLED,
        env_vars: {},
        sg_ids: [],
        param_and_secrets_ext_enabled: false,
        datadog_lambda_apm_enabled: false,
      };
    }
    // Build defaults by merging per-field to ensure all fields are present
    return {
      runtime: propsDefaults.runtime ?? DEFAULT_RUNTIME,
      architecture: propsDefaults.architecture ?? DEFAULT_ARCHITECTURE,
      lambda_source: propsDefaults.lambda_source ?? '',
      memory_size: propsDefaults.memory_size ?? DEFAULT_MEMORY,
      timeout: propsDefaults.timeout ?? DEFAULT_TIMEOUT,
      snapstart: propsDefaults.snapstart ?? defaultSnapstart,
      snap_alias: propsDefaults.snap_alias ?? DEFAULT_SNAP_ALIAS_ENABLED,
      env_vars: propsDefaults.env_vars ?? {},
      sg_ids: propsDefaults.sg_ids ?? [],
      param_and_secrets_ext_enabled: propsDefaults.param_and_secrets_ext_enabled ?? false,
      datadog_lambda_apm_enabled: propsDefaults.datadog_lambda_apm_enabled ?? false,
    };
  }, [propsDefaults, defaultSnapstart]);

  const [defaults, setDefaults] = useState<LambdaDefaults>(initialDefaults);
  const lastPropsDefaultsRef = useRef<string | undefined>(
    propsDefaults ? JSON.stringify(propsDefaults) : undefined,
  );
  const isUpdatingDefaultsRef = useRef(false);
  const defaultsRef = useRef<LambdaDefaults>(initialDefaults);
  const pendingUpdateRef = useRef<string | undefined>(undefined);

  // Keep defaultsRef in sync with defaults state
  useEffect(() => {
    defaultsRef.current = defaults;
  }, [defaults]);

  // Sync with props changes only if props actually changed externally
  useEffect(() => {
    if (isUpdatingDefaultsRef.current) {
      return; // We're updating, skip sync
    }

    const currentPropsString = propsDefaults ? JSON.stringify(propsDefaults) : undefined;

    // Skip if props haven't actually changed (same JSON string)
    if (currentPropsString === lastPropsDefaultsRef.current) {
      return;
    }

    // Skip if these props match what we just sent (our own update coming back)
    if (pendingUpdateRef.current && currentPropsString === pendingUpdateRef.current) {
      lastPropsDefaultsRef.current = currentPropsString;
      pendingUpdateRef.current = undefined;
      return;
    }

    // Props changed externally, sync only if values actually differ from current state
    const currentDefaultsString = JSON.stringify(defaultsRef.current);
    const newDefaultsString = JSON.stringify(initialDefaults);
    if (currentDefaultsString !== newDefaultsString) {
      setDefaults(initialDefaults);
      defaultsRef.current = initialDefaults;
    }
    lastPropsDefaultsRef.current = currentPropsString;
  }, [propsDefaults, initialDefaults]);

  // Update parent when defaults change
  // LambdaFieldsEditor passes partial updates (e.g. { runtime: 'nodejs20.x' }), so we must merge with current defaults
  const updateDefaults = (newDefaults: Partial<LambdaDefaults> | LambdaDefaults) => {
    isUpdatingDefaultsRef.current = true;

    const mergedDefaults = { ...defaults, ...newDefaults } as LambdaDefaults;

    // Preserve env_vars object reference if it hasn't actually changed
    const currentEnvVarsString = JSON.stringify(defaults.env_vars || {});
    const newEnvVarsString = JSON.stringify(mergedDefaults.env_vars || {});
    const preservedDefaults: LambdaDefaults = {
      ...mergedDefaults,
      env_vars:
        currentEnvVarsString === newEnvVarsString ? defaults.env_vars : mergedDefaults.env_vars,
    };

    setDefaults(preservedDefaults);
    defaultsRef.current = preservedDefaults;
    const preservedDefaultsString = JSON.stringify(preservedDefaults);
    lastPropsDefaultsRef.current = preservedDefaultsString;
    pendingUpdateRef.current = preservedDefaultsString;

    onChange(preservedDefaults);

    // Reset flags after parent has had time to update
    setTimeout(() => {
      isUpdatingDefaultsRef.current = false;
      setTimeout(() => {
        pendingUpdateRef.current = undefined;
      }, 200);
    }, 100);
  };

  return (
    <Accordion className={classes.defaultsSection} defaultExpanded={false}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.defaultsSummary}>
        <LambdaDefaultsHeader />
      </AccordionSummary>
      <AccordionDetails className={classes.defaultsDetails}>
        <LambdaFieldsEditor
          value={defaults}
          onChange={updateDefaults}
          isAuthorizer={isAuthorizer}
          showLambdaSource
          showS3Key
          labels={{
            runtime: 'Runtime',
            architecture: 'Architecture',
            memory: 'Memory (MB)',
            timeout: 'Timeout (seconds)',
            envVars: 'Environment Variables',
          }}
        />

        {/* IAM Configuration Section (group-level, not in defaults object) */}
        {onIamPolicyNamesChange && onCustomIamPolicyNamesChange && (
          <LambdaIamSection
            iamPolicyNames={iamPolicyNames}
            customIamPolicyNames={customIamPolicyNames}
            onIamPolicyNamesChange={onIamPolicyNamesChange}
            onCustomIamPolicyNamesChange={onCustomIamPolicyNamesChange}
            description="Configure default IAM policies and permissions for all lambdas"
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};
